import { useCallback, useEffect, useState } from 'react';
import { updateUrlParams } from '../utils/url';

const STORAGE_KEY = 'selected_account_id';
const QUERY_KEY = 'acc';

export type AccountSelectionValue = string | 'ALL' | null;

function getStoredAccountId(): AccountSelectionValue {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as AccountSelectionValue;
  } catch {
    return null;
  }
}

function setStoredAccountId(value: AccountSelectionValue) {
  try {
    if (value) localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    else localStorage.removeItem(STORAGE_KEY);
    window.dispatchEvent(new CustomEvent('accountSelected', { detail: { accountId: value } }));
  } catch {}
}

export function clearStoredAccountId() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    window.dispatchEvent(new CustomEvent('accountSelected', { detail: { accountId: null } }));
  } catch {}
}

export function useAccountSelection(defaultAccountId?: AccountSelectionValue) {
  const getInitial = () => {
    if (typeof window === 'undefined') return defaultAccountId ?? null;
    try {
      const params = new URLSearchParams(window.location.search);
      const fromUrl = params.get(QUERY_KEY);
      if (fromUrl) return fromUrl as AccountSelectionValue;
    } catch {}
    const stored = getStoredAccountId();
    if (stored !== null && stored !== undefined) return stored;
    return defaultAccountId ?? null;
  };

  const [accountId, setAccountId] = useState<AccountSelectionValue>(() => getInitial());

  // An address that names an account (a link, a bookmark, a reload) is the
  // choice: keep it, so the next page — which reads the stored one — agrees.
  useEffect(() => {
    try {
      const fromUrl = new URLSearchParams(window.location.search).get(QUERY_KEY);
      if (fromUrl && getStoredAccountId() !== fromUrl) setStoredAccountId(fromUrl as AccountSelectionValue);
    } catch {}
  }, []);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      try {
        setAccountId(e.newValue ? (JSON.parse(e.newValue) as AccountSelectionValue) : null);
      } catch {
        setAccountId(null);
      }
    };
    const onCustom = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail && 'accountId' in detail) setAccountId(detail.accountId as AccountSelectionValue);
    };
    const onPopState = () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const fromUrl = params.get(QUERY_KEY) as AccountSelectionValue | null;
        // Keep the account when the address names none (see useLocationSelection).
        if (!fromUrl) return;
        setAccountId(fromUrl);
        setStoredAccountId(fromUrl);
      } catch {}
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('accountSelected', onCustom as EventListener);
    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('accountSelected', onCustom as EventListener);
      window.removeEventListener('popstate', onPopState);
    };
  }, []);

  const select = useCallback((id: AccountSelectionValue, options: { replace?: boolean } = {}) => {
    setAccountId(id);
    setStoredAccountId(id);
    try {
      if (id && id !== 'ALL') {
        updateUrlParams({ [QUERY_KEY]: id }, options);
      } else if (id === 'ALL') {
        updateUrlParams({ [QUERY_KEY]: 'ALL' }, options);
      } else {
        updateUrlParams({ [QUERY_KEY]: null }, options);
      }
    } catch {}
  }, []);

  return { accountId, select };
}
